import type { Hypothesis, HypothesisConfig } from '../api/useHypotheses.ts';
import { Ikon } from '../status/Ikon.tsx';
import { Utgangspunkt } from './Betaprogram.tsx';
import { hypoteseOppforing } from '../status/status.ts';
import { baselineLaast, eierDeaktivert, fornavn, genitiv, hvem, navnAv, overstyring, sendtTilbake, statusNavn, stor, ventendeJustering } from './lesning.ts';
import { kortDato, kortDatoAv, tall } from './setning.ts';

/**
 * Det detaljen viser etter «valgt» (H2.3 andre runde, `admin-hypoteser.jsx`): sjekklista før
 * start der låsene har navn (P6), funnene (P7), og konklusjonen med tallene og de obligatoriske
 * radene (P8, P8b). Rene visninger — handlingene og arkene eies av `HDetalj`.
 */

type Tilstand = 'ok' | 'laas' | 'aapen';
const IKON: Record<Tilstand, string> = { ok: 'checkmark-outline', laas: 'lock-closed-outline', aapen: 'ellipse-outline' };

/**
 * «Før start — Samiras liste» (P6): én rad per port i `HypothesisStartRequest`, svaret på samme
 * linje. En lås har ikon OG ord, og sier hvem som kan løsne den; kilden som må bygges sier at
 * ingen kan (DD-08, «Forklarte låser»). Bare eieren starter.
 */
export function Sjekkliste({ h, config, megId }: { h: Hypothesis; config: HypothesisConfig | undefined; megId: string | undefined }) {
  const eierF = h.owner?.id === megId ? 'du' : fornavn(h.owner);
  const tittel = h.owner?.id === megId ? 'Før start — din liste' : `Før start — ${genitiv(fornavn(h.owner))} liste`;
  const avgjoer = hvem(h.decider, megId, 'du');
  const kanLoesne = avgjoer === 'du' ? 'du kan løsne' : `${avgjoer} kan løsne`;
  const rader: Array<[string, string, Tilstand]> = [];

  const deakt = eierDeaktivert(h, config);
  if (h.ownerAcceptedAt) rader.push(['Eierskapet', `tatt ${kortDatoAv(h.ownerAcceptedAt)}${deakt ? ' · eieren er deaktivert' : ''}`, deakt ? 'laas' : 'ok']);
  else rader.push(['Eierskapet', deakt ? 'ikke tatt · eieren er deaktivert' : `ikke tatt ennå · venter på ${eierF === 'du' ? 'deg' : eierF}`, 'laas']);

  if (ventendeJustering(h)) rader.push(['Justering', `venter på ${hvem(h.decider, megId, 'deg')}`, 'laas']);

  const kildeLoesnet = overstyring(h, 'uavklart_kilde');
  if (h.sourceStatus === 'maa_bygges') {
    const bygger = h.measurementOwner ? ` · ${hvem(h.measurementOwner, megId, 'du')} bygger målingen${h.measurementReadyBy ? ` innen ${kortDato(h.measurementReadyBy)}` : ''}` : '';
    rader.push(['Kilden', `må bygges${bygger} · kan ikke løsnes`, 'laas']);
  } else if (h.sourceStatus === 'verifisert') {
    rader.push(['Kilden', `verifisert${h.sourceVerifiedBy ? ` av ${hvem(h.sourceVerifiedBy, megId, 'deg')}` : ''}`, 'ok']);
  } else if (kildeLoesnet) {
    rader.push(['Kilden', `løsnet av ${hvem(kildeLoesnet.by, megId, 'deg')}`, 'ok']);
  } else {
    rader.push(['Kilden', `uavklart · ${kanLoesne}`, 'laas']);
  }

  const baseLoesnet = overstyring(h, 'baseline_mangler');
  if (baselineLaast(h)) rader.push(['Baseline', `ikke målt · ${kanLoesne}`, 'laas']);
  else if (baseLoesnet) rader.push(['Baseline', `løsnet av ${hvem(baseLoesnet.by, megId, 'deg')}`, 'ok']);
  else rader.push(['Baseline', `${h.baselineValue != null ? `${tall(h.baselineValue)} ${h.unit ?? ''}`.trim() : 'målt'}${h.baselineOn ? ` · målt ${kortDato(h.baselineOn)}` : ''}`, 'ok']);

  rader.push(['Det vi ikke gjør', h.notDoing ? 'skrevet' : 'ikke skrevet', h.notDoing ? 'ok' : 'aapen']);
  rader.push(['Testperiode', h.testStartsOn && h.testEndsOn ? `${kortDato(h.testStartsOn)}–${kortDato(h.testEndsOn)}` : h.testEndsOn ? `foreslått til ${kortDato(h.testEndsOn)}` : 'ikke satt', h.testStartsOn ? 'ok' : 'aapen']);

  return (
    <div role="group" aria-label={tittel} className="hb-sjekkliste" data-testid="hb-sjekkliste">
      <h3 className="panel-h3">{tittel}</h3>
      <ul className="hb-sjekk-rader">
        {rader.map(([navn, svar, t]) => (
          <li key={navn} className={`hb-sjekk-rad hb-sjekk-${t}`}>
            <Ikon navn={IKON[t]} className="hb-sjekk-ikon" />
            <span>
              <b>{navn}</b> <span className="hb-sjekk-svar">{svar}</span>
            </span>
          </li>
        ))}
      </ul>
      <p className="hb-dempet">{eierF === 'du' ? 'Bare du starter.' : `Bare ${eierF} starter.`}</p>
    </div>
  );
}

/** Funnene under og etter testen (P7): målepunkt og observasjon, nyeste først. Rettelsene står i konklusjonen. */
export function Funnliste({ h, megId }: { h: Hypothesis; megId: string | undefined }) {
  const funn = h.findings.filter((f) => f.kind !== 'rettelse').slice().reverse();
  if (funn.length === 0) return null;
  return (
    <div className="hb-funn">
      <h3 id="hb-funn-h" className="panel-h3">
        Funn · {funn.length}
      </h3>
      <ol className="hb-logg" aria-label="Funn">
        {funn.map((f) => (
          <li key={f.id} className="hb-logg-rad">
            <span className="hb-fet">{f.kind === 'maalepunkt' ? `Målepunkt · ${f.value != null ? `${tall(f.value)} ${h.unit ?? ''}`.trim() : '—'}` : 'Observasjon'}</span>
            <span className="hb-liten">{f.measuredOn ? kortDato(f.measuredOn) : kortDatoAv(f.at)}</span>
            <span className="hb-dempet hb-logg-detalj">{f.text ? `${f.text} — ${hvem(f.by, megId, 'du')}` : hvem(f.by, megId, 'du')}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

/**
 * Konklusjonen (P8 venter, P8b fryst) eller stoppet, som konseptsidens skjerm 8 og 8b: tallrekken
 * først — resultatet er helten, «I dag» dempet, terskelstreken foran «Rett hvis …» —, så utfallet
 * som ord med ikon og aldri som pille før det er bekreftet (avvik 35; de tre har samme farge og
 * vekt, H20), og radene. «Åpne blokkerende funn» og «Hva dataene ikke tåler» står alltid (DD-35);
 * uenigheten er alltid et felt, tomt som «Ingen uenighet ført.» (H19). Rettelsene etter frysingen
 * er funn. Linjen med pillen og «skrevet av …» / «bekreftet … · fryst» eies av detaljen.
 */
export function Konklusjonsblokk({ h, config, megId, avgjoer }: { h: Hypothesis; config: HypothesisConfig | undefined; megId: string | undefined; avgjoer: boolean }) {
  const k = h.conclusion;
  if (!k) return null;
  const fryst = Boolean(k.frozenAt);
  const utfall = k.outcome ? statusNavn(config, { status: 'konkludert', outcome: k.outcome }).toLowerCase() : '';
  const stoppkode = k.stopReason ? config?.stopReasonWording.find((w) => w.code === k.stopReason)?.label : undefined;
  const tilbake = sendtTilbake(h);
  const rader: Array<[string, string]> = [
    [k.kind === 'stopp' ? 'Hva vi rakk å lære' : 'Hva vi lærte', k.learned],
    ['Åpne blokkerende funn', k.openBlocking],
    ['Hva dataene ikke tåler', k.dataLimitations],
  ];
  if (k.nextSteps) rader.push(['Neste steg', k.nextSteps]);
  if (k.kind === 'konklusjon') rader.push(['Uenighet', k.dissent?.trim() ? k.dissent : 'Ingen uenighet ført.']);
  for (const f of h.findings) if (f.kind === 'rettelse') rader.push(['Rettelse', `${stor(navnAv(f.by))} · ${kortDatoAv(f.at)} — ${f.text ?? ''}`]);

  return (
    <section className="hb-konklusjon" aria-label={k.kind === 'stopp' ? 'Stoppet' : 'Konklusjonen'}>
      {stoppkode ? <p className="hb-utfall">{`Stoppkode: ${stoppkode}`}</p> : null}
      {k.kind === 'konklusjon' ? <Tallrekke h={h} medDato /> : null}
      {k.kind === 'konklusjon' && k.outcome && !fryst ? (
        <p className="hb-utfall">
          <Ikon navn={hypoteseOppforing(k.outcome).ikon} />
          <span className="hb-fet">{`${k.writtenBy.id === megId ? 'Ditt' : stor(genitiv(fornavn(k.writtenBy)))} utfall: ${utfall}`}</span>
          {avgjoer && !tilbake ? <span className="hb-dempet">— bekreft, eller send tilbake med merknad.</span> : null}
        </p>
      ) : null}
      {tilbake && !fryst ? (
        <p className="hb-kort" data-testid="hb-sendt-tilbake">
          {`Sendt tilbake til ${hvem(h.owner, megId, 'deg')} ${kortDatoAv(k.sentBackAt!)}: «${k.sentBackNote ?? ''}»`}
        </p>
      ) : null}
      <dl className="hb-fakta hb-konklusjon-rader">
        {rader.map(([navn, verdi], i) => (
          <div key={`${navn}-${i}`} className="hb-fakta-rad">
            <dt>{navn}</dt>
            <dd>{verdi}</dd>
          </div>
        ))}
        {/* H3.4 (skjerm 15, 16): rapporten konklusjonen bygde på. Radene over er eierens tekst etter endringene. */}
        {k.kind === 'konklusjon' && k.programReport ? <Utgangspunkt r={k.programReport} outcome={k.outcome} /> : null}
      </dl>
    </section>
  );
}

/**
 * Tallrekken (konseptets skjerm 8 og 8b, M10c): «I dag» dempet, resultatet som helten, og
 * terskelstreken foran «Rett hvis …» — strek og ord, aldri farge alene. Baselinen og terskelen er
 * gjeldende form, resultatet konklusjonens. Med `medDato` står målingsdatoen ved «Resultat»
 * (detaljen); innsenderens svar (M10c) har bare ordet, og tallene «etter størrelse» — resultatet
 * størst, «I dag» og terskelen mindre.
 */
export function Tallrekke({ h, medDato = false, etterStoerrelse = false }: { h: Hypothesis; medDato?: boolean; etterStoerrelse?: boolean }) {
  const k = h.conclusion;
  const enhet = (v: number) => `${tall(v)} ${h.unit ?? ''}`.trim();
  // Tjeneren sender null der kontrakten sier utelatt (H2.5-funn 3): `!= null`, aldri `!== undefined`.
  const idag = h.baselineValue != null ? enhet(h.baselineValue) : h.baselineState === 'ikke_maalt' ? 'Ikke målt' : null;
  return (
    <ul className={etterStoerrelse ? 'hb-tallrekke hb-tallrekke-stoerrelse' : 'hb-tallrekke'} aria-label="Tallene">
      {idag !== null ? (
        <li className="hb-tall hb-tall-idag">
          <span className="hb-tall-etikett">I dag</span>
          <span className="hb-tall-verdi">{idag}</span>
        </li>
      ) : null}
      {k?.resultValue != null ? (
        <li className="hb-tall hb-tall-resultat">
          <span className="hb-tall-etikett">{`Resultat${medDato && k.resultMeasuredOn ? ` · ${kortDato(k.resultMeasuredOn)}` : ''}`}</span>
          <span className="hb-tall-verdi">{enhet(k.resultValue)}</span>
        </li>
      ) : null}
      {h.threshold != null ? (
        <li className="hb-tall hb-tall-terskel">
          <span className="hb-tall-etikett">{`Rett hvis ${h.direction === 'hoyst' ? 'høyst' : 'minst'}`}</span>
          <span className="hb-tall-verdi">{enhet(h.threshold)}</span>
        </li>
      ) : null}
    </ul>
  );
}
